import React, { useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import useProjectStore from '../../../../../ProjectCanvas/useProjectStore';
import { CanvasContext } from '../../../WorkflowCanvas';
import CanvasReloadDialog from '../../../CanvasReloadDialog';
import ComponentSettingsToolbar from './ComponentNodeUtil/ComponentSettingsToolbar';
import InlineNodeView from './InlineNodeView';
import InlineConfigPanel from '../../../ReactFlowUtil/InlineConfigPanel';
import { withStyles } from '@mui/styles';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EditIcon from '@mui/icons-material/Edit';
import useCanvasStore from '../../../useCanvasStore';
import InstructionsInfo from '../../../InstructionInfo';
import { styles } from './ComponentNodeUtil/CustomComponentNodeUtil';
import BaseComponent from './BaseComponent';
import { send_request } from '../../../../../../utils/Request';

const ProductionCanvasComponent = ({ id, data, classes, selected, xPos, yPos }) => {
    const { modeInfo } = useContext(CanvasContext);
    if (modeInfo === undefined) {
      return <BaseComponent classes={classes} data={data} isConnectable={false} />;
    }

    const location = useLocation();
    const search = useLocation().search;
    const queryParams = new URLSearchParams(search);

    const parentRef = useRef(null);
    const ref = useRef(null);
    const { forceStart, handleProductionCanvasMount } = useContext(CanvasContext);
    const { productionVersion } = useContext(CanvasContext);
    const params = useParams();
    const projectId = params.id;
    const navigate = useNavigate();

    const [forceStartComponent, setForceStartComponent] = forceStart ? forceStart : useState({});
    const [isSelectedForceStart, setIsSelectedForceStart] = useState(false);
    const [showCheckingLatestDialog, setShowCheckingLatestDialog] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
      setIsSelectedForceStart(
        selected || (forceStartComponent && !selected && id === forceStartComponent.componentId)
      );
    }, [forceStartComponent, selected, id]);

    function handleCloseForceStartPane() {
      if (window.location.href.includes('?force_start='))
        navigate(`/project/production/${projectId}`);
      setForceStartComponent(null);
    }

    const getLatestVersion = async projectId => {
      return send_request(`project-service/project/last-version/${projectId}`, '', '', 'get').then(
        res => {
          if (!res || !res.data) return;
          let { draftVersion } = res.data;
          if (draftVersion) {
            useProjectStore.setState({ draftVersion });
          }

          let componentId = queryParams.get('force_start');
          if (!componentId) return;

          let openedComponent = useProjectStore.getState().canvas?.find(comp => comp.id === componentId);

          if (!openedComponent) {
            navigate(`/project/production/${projectId}`);
            return;
          }

          setForceStartComponent({
            componentId: componentId,
            ...openedComponent.data,
          });
        }
      );
    };

    const handleNodeClick = useCallback(
      event => {
        event.stopPropagation();
        if (forceStartComponent) {
          handleCloseForceStartPane();
          return;
        }

        queryParams.set('force_start', id);
        let newUrl = `${location.pathname}?${queryParams.toString()}`;
        navigate(newUrl);
        getLatestVersion(projectId);
      },
      [forceStart, forceStartComponent, data, id]
    );

    const handleRun = useCallback(() => {
      if (!data) return;
      setShowCheckingLatestDialog(true);
    }, [data]);

    const handleOpenEditable = async () => {
      let url;
      if (data.fromWorkflowPath) url = `/project/canvas/${projectId}?view=${data.parentId}`;
      else url = `/project/canvas/${projectId}?view=${id}`;

      useProjectStore.setState({ canvasView: false, production: false });
      navigate(url);
    };

    const nodeSettingsMenuItems = [
      {
        title: 'Run from this component',
        text: 'Run',
        icon: <PlayCircleIcon />,
        onClick: handleRun,
      },
      {
        title: 'Edit this component',
        icon: <EditIcon />,
        onClick: handleOpenEditable,
      },
    ];

    return (
      <>
        <div
          draggable={false}
          ref={parentRef}
          onDragOver={event => event.preventDefault()}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ width: 520, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          onClick={handleNodeClick}
        >
          <InlineNodeView
            data={data}
            nodeId={id}
            saving={false}
            isHovered={isHovered}
            addLinkedConditionalPath={() => {}}
            currentXPosition={xPos}
            currentYPosition={yPos}
            readOnly
          >
            <InlineConfigPanel
              componentId={id}
              componentType={data.type}
              projectId={projectId}
              version={productionVersion}
              nodeData={data}
              readOnly
            />
          </InlineNodeView>

          <ComponentSettingsToolbar
            selected={selected || isHovered}
            actions={nodeSettingsMenuItems}
            classes={classes}
            data={data}
          />

          {data.instructions &&
            (data.instructions.buildInstruction || data.instructions.editInstruction) && (
              <InstructionsInfo data={data} parent={parentRef} textRef={ref} />
            )}
        </div>
        {showCheckingLatestDialog && (
          <CanvasReloadDialog
            projectId={projectId}
            currentProdVersion={productionVersion}
            setShowCheckingLatestDialog={setShowCheckingLatestDialog}
            data={data}
            id={id}
            forceStartComponent={forceStartComponent}
            setForceStartComponent={setForceStartComponent}
            handleProductionCanvasMount={handleProductionCanvasMount}
          />
        )}
      </>
    );
};

export default withStyles(styles)(ProductionCanvasComponent);
